import { useCallback, useMemo, useRef } from 'react';

import { SnackbarKey } from 'notistack';
import { useNavigate } from 'react-router';

import useDateText, { DateTextVariants } from '@/components/DateText/useDateText';
import { MAX_SELECTED_SYMBOLS } from '@/constants/constants';
import { useAppDispatch, useAppSelector } from '@/hooks/redux-hooks';
import useSnackbarNotifications, { SnackbarMessage } from '@/hooks/useSnackbarNotifications';
import { ExportSettingsType } from '@/pages/DataHub/Export/ExportSettingsContext';
import { separator } from '@/pages/DataHub/Preview/constants';
import { saveDataHub, saveDataHubs, setActiveDataHubId } from '@/slices/dataHubSlice';
import { clearDataHub, deleteSymbolFromDataHub, fetchDhData } from '@/slices/dhDataSlice';
import { openModal } from '@/slices/modalSlice';
import { clearSelection } from '@/slices/symbolSelectorSlice';
import { DataHub } from '@/types/dataHub';
import { SelectedSymbol } from '@/types/symbols';
import { generateUniqueUUID } from '@/utils/generateUniqueUUID';

interface ApiCallOptions {
  call: () => Promise<unknown>;
  message: SnackbarMessage;
  onSuccess?: () => void;
  onError?: () => void;
}

const getItemsAddedMessage = (
  difference: number,
  numberOfSymbolsAlreadyInDataHub: number
): string => {
  let message = `${difference} ${difference === 1 ? 'item' : 'items'} added!`;

  if (numberOfSymbolsAlreadyInDataHub > 0) {
    message = `${numberOfSymbolsAlreadyInDataHub} ${numberOfSymbolsAlreadyInDataHub === 1 ? 'item' : 'items'} already in Data Hub. ${message}`;
  }
  return message;
};

/**
 * Hook for managing Data Hub selection functionality
 */
const useDataHubSelector = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const symbolSelectorState = useAppSelector(state => state.symbolSelector);
  const { symbols: selectedSymbols } = symbolSelectorState;
  const { dataHubs, activeDataHubId } = useAppSelector(state => state.dataHub);
  const { formatDateString } = useDateText();

  const { showErrorSnackbar, showLoadingSnackbar, showSuccessSnackbar } =
    useSnackbarNotifications();
  const snackIdRef = useRef<SnackbarKey | null>(null);

  // Find the active data hub from the list of data hubs
  const activeDataHub = useMemo(
    () => dataHubs.find(dataHub => dataHub.id === activeDataHubId),
    [dataHubs, activeDataHubId]
  );

  // Destructure properties from active data hub with defaults
  const { symbols = [], name = '', exportSettings } = activeDataHub ?? {};

  /**
   * Execute API calls with standardized loading, success, and error handling
   */
  const executeApiCall = useCallback(
    async ({ call, message, onSuccess, onError }: ApiCallOptions): Promise<void> => {
      const id = showLoadingSnackbar(message.loading);
      snackIdRef.current = id;

      try {
        await call();

        if (snackIdRef.current === id) {
          snackIdRef.current = showSuccessSnackbar(message.success);
        }

        if (onSuccess) onSuccess();
      } catch {
        snackIdRef.current = showErrorSnackbar(message.error);
        if (onError) onError();
      }
    },
    [showLoadingSnackbar, snackIdRef, showSuccessSnackbar, showErrorSnackbar]
  );

  /**
   * Updates and saves a data hub with the new symbols
   */
  const updateAndSave = useCallback(
    async (
      dataHub: DataHub,
      updatedSymbolArray: SelectedSymbol[],
      difference: number,
      numberOfSymbolsAlreadyInDataHub: number
    ): Promise<void> => {
      // Create a deep copy to avoid mutation
      const updatedDataHub: DataHub = {
        ...JSON.parse(JSON.stringify(dataHub)),
        symbols: updatedSymbolArray,
        savedAt: new Date().toISOString()
      };

      await executeApiCall({
        call: () => dispatch(saveDataHub(updatedDataHub)).unwrap(),
        message: {
          loading: 'Items are adding, please wait...',
          error: "Oops! We couldn't save your dataHub. Please try again.",
          success: getItemsAddedMessage(difference, numberOfSymbolsAlreadyInDataHub)
        },
        onSuccess: () => {
          dispatch(clearSelection());
          dispatch(fetchDhData({ symbols: updatedSymbolArray }));
          dispatch(setActiveDataHubId(dataHub.id));
          navigate('/data-hub/summary');
        }
      });
    },
    [dispatch, executeApiCall, navigate]
  );

  /**
   * Creates a unique data hub name based on the current date
   */
  const createDataHubName = useCallback((): string => {
    const formattedDate = formatDateString(new Date().toISOString(), DateTextVariants.FULL);
    let newName = `My Data ${formattedDate}`;

    // Find existing numbers in data hub names
    const existingNumbers = dataHubs
      .map(({ name }) => name)
      .filter(name => name.startsWith(newName))
      .map(name => {
        const match = /\((\d+)\)$/.exec(name);
        return match ? parseInt(match[1], 10) : 0;
      });

    // Start counter from next available number
    let counter = existingNumbers.length > 0 ? Math.max(...existingNumbers) + 1 : 1;

    // Ensure name is unique
    while (dataHubs.some(({ name }) => name.toLowerCase() === newName.toLowerCase())) {
      newName = `${newName} (${counter})`;
      counter++;
    }

    return newName;
  }, [dataHubs, formatDateString]);

  /**
   * Creates a new data hub with the selected symbols
   */
  const createNewDataHub = useCallback(async (): Promise<void> => {
    const newUuid = generateUniqueUUID(new Set());
    const symbols = symbolSelectorState?.symbols;

    await executeApiCall({
      call: () =>
        dispatch(
          saveDataHubs([
            {
              savedAt: new Date().toISOString(),
              symbols,
              name: createDataHubName(),
              id: newUuid,
              exportSettings: {
                from: null,
                to: null,
                periodNumber: '2',
                period: 'm',
                exportType: 'all',
                includeMetadata: false
              }
            },
            ...dataHubs
          ])
        ).unwrap(),
      message: {
        loading: 'Creating new Data Hub...',
        success: 'Data Hub created!',
        error: "Oops! We couldn't create your dataHub. Please try again."
      },
      onSuccess: () => {
        dispatch(setActiveDataHubId(newUuid));
        dispatch(clearSelection());
        dispatch(fetchDhData({ symbols }));
        navigate('/data-hub/summary');
      }
    });
  }, [
    createDataHubName,
    dataHubs,
    dispatch,
    executeApiCall,
    navigate,
    symbolSelectorState.symbols
  ]);

  /**
   * Deletes a data hub by ID
   */
  const deleteDataHub = useCallback(
    async (id: string) => {
      const dataHubsToRemain = dataHubs.filter(dataHub => dataHub.id !== id);

      await executeApiCall({
        call: () => dispatch(saveDataHubs(dataHubsToRemain)).unwrap(),
        message: {
          loading: 'Deleting Data Hub...',
          success: 'Data Hub deleted!',
          error: "Oops! We couldn't delete your dataHub. Please try again."
        },
        onSuccess: () => {
          dispatch(setActiveDataHubId(''));
          dispatch(clearDataHub());
        }
      });
    },
    [dataHubs, dispatch, executeApiCall]
  );

  /**
   * Deletes a column from the active data hub
   */
  const deleteColumnFromDataHub = useCallback(
    async (columnKey: string): Promise<void> => {
      const [timeRef, period, periodType, code] = columnKey.split(separator);

      // Filter out the symbol that matches the column key
      const newSymbols = symbols.filter(
        symbol =>
          symbol.TimeRef !== timeRef ||
          symbol.Period !== period ||
          symbol.PeriodType !== periodType ||
          symbol.Code !== code
      );

      await executeApiCall({
        call: () =>
          dispatch(
            saveDataHub({
              savedAt: new Date().toISOString(),
              name,
              id: activeDataHubId,
              symbols: newSymbols,
              exportSettings: exportSettings as ExportSettingsType
            })
          ).unwrap(),
        message: {
          loading: 'Removing item from Data Hub...',
          success: 'Item removed from Data Hub!',
          error: "Oops! We couldn't remove item from your dataHub. Please try again."
        },
        onSuccess: () => {
          dispatch(deleteSymbolFromDataHub(columnKey));
        }
      });
    },
    [activeDataHubId, dispatch, executeApiCall, exportSettings, name, symbols]
  );

  /**
   * Adds selected symbols to an existing data hub
   */
  const addSymbolsToExistingDataHub = useCallback(
    async (id: string): Promise<void> => {
      const targetDataHub = dataHubs.find(dataHub => dataHub.id === id);

      if (!targetDataHub) return;

      const symbolsInDataHub = targetDataHub.symbols;

      // Combine symbols and ensure uniqueness using a Map
      const updatedSymbolArray = Array.from(
        new Map(
          [...symbolsInDataHub, ...selectedSymbols].map(symbol => [
            `${symbol.Code}${symbol.Period}${symbol.PeriodType}${symbol.TimeRef}`,
            symbol
          ])
        ).values()
      );

      // Count symbols that are already in the data hub
      const numberOfSymbolsAlreadyInDataHub = symbolsInDataHub.filter(symbol =>
        selectedSymbols.some(
          selected =>
            selected.Code === symbol.Code &&
            selected.Period === symbol.Period &&
            selected.PeriodType === symbol.PeriodType &&
            selected.TimeRef === symbol.TimeRef
        )
      ).length;

      // Check if limit is reached
      if (updatedSymbolArray.length > MAX_SELECTED_SYMBOLS) {
        dispatch(
          openModal({
            title: 'Limit reached!',
            body: `You can add up to ${MAX_SELECTED_SYMBOLS} items to your Data Hub. `
          })
        );
        return;
      }

      // Calculate difference in symbol count
      const difference = updatedSymbolArray.length - symbolsInDataHub.length;

      // If no new symbols are added, just navigate to data hub
      if (difference < 1) {
        dispatch(clearSelection());
        snackIdRef.current = showSuccessSnackbar(
          getItemsAddedMessage(0, numberOfSymbolsAlreadyInDataHub)
        );
        dispatch(setActiveDataHubId(id));
        navigate('/data-hub/summary');
      } else {
        await updateAndSave(
          targetDataHub,
          updatedSymbolArray,
          difference,
          numberOfSymbolsAlreadyInDataHub
        );
      }
    },
    [dataHubs, dispatch, navigate, selectedSymbols, showSuccessSnackbar, snackIdRef, updateAndSave]
  );

  return {
    deleteDataHub,
    addSymbolsToExistingDataHub,
    createNewDataHub,
    deleteColumnFromDataHub
  };
};

export default useDataHubSelector;
